import {
    type ImageStyle,
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    FlagIconSizeConstantType,
    FlagCachedConstantType,
    FlagLabelConstantType,
    FlagDynamicStyleType,
    FlagCodeConstantType,
    FlagUriConstantType
} from "./type";
import FLAG_LOADERS from "./flags";
import type {
    Mutable
} from "../../types";

export const FLAG_CODE_PATTERN = /^[a-z]{2}[a-z_]*(-[a-z0-9_]+)*$/;

const FLAG_EMBEDDED_PREFIX = "ncore-flag:";

export const FLAG_XML_CACHE = new Map<string, string>();

const FLAG_BLOCKED_CODES = new Set([
    "il"
]);

export const getIsFlagBlocked = ({
    code
}: FlagCodeConstantType): boolean => {
    return FLAG_BLOCKED_CODES.has(code.trim().toLowerCase().split("-")[0] ?? "");
};

const FLAG_NAME_FORMATTERS = new Map<string, Intl.DisplayNames>();

export const getFlagCode = ({
    code
}: FlagCodeConstantType): string => {
    return code.trim().toLowerCase();
};

export const getFlagUri = ({
    baseUrl,
    code
}: FlagUriConstantType): string | undefined => {
    if(!code || !FLAG_CODE_PATTERN.test(code) || getIsFlagBlocked({
        code
    })) {
        return undefined;
    }

    if(!baseUrl) {
        return FLAG_LOADERS[code] ? `${FLAG_EMBEDDED_PREFIX}${code}` : undefined;
    }

    return `${baseUrl.replace(/\/+$/, "")}/${code}.svg`;
};

export const getIsFlagEmbedded = ({
    uri
}: FlagCachedConstantType): boolean => {
    return !!uri && uri.startsWith(FLAG_EMBEDDED_PREFIX);
};

export const loadEmbeddedFlag = async ({
    uri
}: FlagCachedConstantType): Promise<string> => {
    const loader = uri ? FLAG_LOADERS[uri.slice(FLAG_EMBEDDED_PREFIX.length)] : undefined;

    if(!loader) {
        throw new Error(`Flag: "${uri}" is not in the kit.`);
    }

    return (await loader()).default;
};

export const getFlagImageUri = ({
    uri
}: FlagCachedConstantType): string | undefined => {
    if(!getIsFlagEmbedded({
        uri
    })) {
        return uri;
    }

    const xml = uri ? FLAG_XML_CACHE.get(uri) : undefined;

    return xml ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}` : undefined;
};

export const getFlagLabel = ({
    locale,
    code
}: FlagLabelConstantType): string => {
    if(code && getIsFlagBlocked({
        code
    })) {
        return "";
    }

    const upperCode = (code ?? "").toUpperCase();

    if(!upperCode || typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
        return upperCode;
    }

    try {
        let formatter = FLAG_NAME_FORMATTERS.get(locale);

        if(!formatter) {
            formatter = new Intl.DisplayNames([
                locale
            ], {
                fallback: "none",
                type: "region"
            });

            FLAG_NAME_FORMATTERS.set(locale, formatter);
        }

        return formatter.of(upperCode) ?? upperCode;
    } catch {
        return upperCode;
    }
};

export const getFlagCached = ({
    uri
}: FlagCachedConstantType): boolean => {
    if(!uri) {
        return false;
    }

    if(Platform.OS !== "web" || getIsFlagEmbedded({
        uri
    })) {
        return FLAG_XML_CACHE.has(uri);
    }

    if(typeof globalThis.Image !== "function") {
        return false;
    }

    const probe = new globalThis.Image();

    probe.src = uri;

    return probe.complete && probe.naturalWidth > 0;
};

export const getFlagIconSize = ({
    size
}: FlagIconSizeConstantType): number | undefined => {
    if(typeof size === "number") {
        return size;
    }

    const parsedSize = Number.parseFloat(size ?? "");

    return Number.isFinite(parsedSize) ? parsedSize : undefined;
};

const stylesheet = StyleSheet.create({
    placeholder: {
        ...StyleSheet.absoluteFill
    },
    container: {
        justifyContent: "center",
        alignItems: "center"
    }
});

export const useStyles = ({
    placeholderColor,
    size
}: FlagDynamicStyleType) => {
    const styles = {
        placeholder: {
            backgroundColor: placeholderColor,
            borderRadius: size / 2
        } as Mutable<ViewStyle>,
        container: {
            height: size,
            width: size
        } as Mutable<ViewStyle>,
        image: {
            height: size,
            width: size
        } as Mutable<ImageStyle>
    };

    return styles;
};
export default stylesheet;
